/**
 * The rave's soundtrack, made on the spot with Web Audio: a 128 BPM four-on-
 * the-floor with off-beat hats, claps, a pumping bass, chord stabs, a riser and
 * snare roll into each drop, and air horns on the drops. Nothing is downloaded.
 * The timeline (PLAN, in beats) is shared with the visuals in rave.ts.
 */

export const BPM = 128;
/** Seconds per beat. */
export const BEAT = 60 / BPM;

/** When each part starts, in beats from the first one. */
export const PLAN = {
  /** Lights down, the ball comes in, a riser and snare roll, then a countdown. */
  build: 0,
  drop: 8,
  /** No kick for a bar: hands up, another roll. */
  breakdown: 40,
  drop2: 44,
  /** Lights up. */
  out: 60,
  /** Everything gone. */
  end: 64,
} as const;

// A minor, F, C, G, one chord a bar.
const CHORDS = [
  [220, 261.63, 329.63],
  [174.61, 220, 261.63],
  [261.63, 329.63, 392],
  [196, 246.94, 293.66],
];
const BASS = [55, 43.65, 65.41, 49];

export interface RaveBeat {
  /** Bars for the equaliser, 0 to 255 each, or null before the sound starts. */
  levels(): Uint8Array | null;
  /** Whether the browser is holding the sound back until the person clicks something. */
  blocked(): boolean;
  /** Try again after a click (browsers only let sound start from one). */
  unblock(): Promise<boolean>;
  stop(): void;
}

/**
 * Start the soundtrack so beat 0 lands at `beat0` (a performance.now() time).
 * If the browser holds it back, nothing plays until unblock() works, and then
 * it joins in on the next beat.
 */
export function playRaveBeat(beat0: number): RaveBeat {
  const ctx = new AudioContext();
  const master = ctx.createGain();
  master.gain.value = 0.5;
  const squash = ctx.createDynamicsCompressor();
  squash.threshold.value = -14;
  squash.ratio.value = 6;
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 128;
  analyser.smoothingTimeConstant = 0.6;
  master.connect(squash).connect(analyser).connect(ctx.destination);

  const noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const samples = noise.getChannelData(0);
  for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1;

  let scheduled = false;
  let stopped = false;
  const bins = new Uint8Array(analyser.frequencyBinCount);

  function env(gain: GainNode, t: number, peak: number, decay: number, attack = 0.003) {
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  }

  function hiss(t: number, length: number, filter: BiquadFilterType, freq: number, peak: number, decay: number, q = 1) {
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const f = ctx.createBiquadFilter();
    f.type = filter;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    env(g, t, peak, decay);
    src.connect(f).connect(g).connect(master);
    src.start(t, Math.random() * 0.5, length);
    return f;
  }

  function tone(type: OscillatorType, t: number, freq: number, peak: number, decay: number, detune = 0, attack = 0.003) {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    o.detune.value = detune;
    const g = ctx.createGain();
    env(g, t, peak, decay, attack);
    o.connect(g).connect(master);
    o.start(t);
    o.stop(t + attack + decay + 0.05);
    return o;
  }

  function kick(t: number, peak = 1) {
    const o = tone("sine", t, 160, peak, 0.38);
    o.frequency.exponentialRampToValueAtTime(42, t + 0.11);
    hiss(t, 0.01, "highpass", 3000, 0.25 * peak, 0.008);
  }

  const hat = (t: number, open = false) => hiss(t, open ? 0.25 : 0.06, "highpass", 8000, open ? 0.2 : 0.14, open ? 0.18 : 0.035);

  function clap(t: number, peak = 0.5) {
    for (let i = 0; i < 3; i++) hiss(t + i * 0.011, 0.04, "bandpass", 1300, peak * (i === 2 ? 1 : 0.6), i === 2 ? 0.17 : 0.02, 1.2);
  }

  function bass(t: number, freq: number) {
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.value = freq;
    const f = ctx.createBiquadFilter();
    f.type = "lowpass";
    f.Q.value = 9;
    f.frequency.setValueAtTime(1400, t);
    f.frequency.exponentialRampToValueAtTime(220, t + BEAT * 0.45);
    const g = ctx.createGain();
    env(g, t, 0.42, BEAT * 0.45, 0.008);
    o.connect(f).connect(g).connect(master);
    o.start(t);
    o.stop(t + BEAT * 0.5 + 0.05);
  }

  function stab(t: number, chord: number[], peak = 0.07, decay = 0.28) {
    for (const freq of chord) for (const cents of [-14, 0, 14]) tone("sawtooth", t, freq * 2, peak, decay, cents, 0.005);
  }

  function arp(t: number, chord: number[], step: number) {
    tone("square", t, chord[step % 3] * (step % 6 < 3 ? 4 : 2), 0.045, BEAT * 0.2);
  }

  function crash(t: number) {
    hiss(t, 2, "highpass", 4500, 0.32, 1.6);
  }

  /** "Baaap bap bap BAAAAAP". */
  function airhorn(t: number) {
    const blasts: Array<[number, number]> = [
      [0, 0.12],
      [0.19, 0.08],
      [0.33, 0.08],
      [0.5, 0.75],
    ];
    const shape = ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) curve[i] = Math.tanh((i / 128 - 1) * 4);
    shape.curve = curve;
    const tilt = ctx.createBiquadFilter();
    tilt.type = "peaking";
    tilt.frequency.value = 1800;
    tilt.gain.value = 8;
    const out = ctx.createGain();
    out.gain.value = 0.16;
    shape.connect(tilt).connect(out).connect(master);
    for (const [at, length] of blasts) {
      for (const [freq, cents] of [
        [466, -8],
        [466, 9],
        [554, 0],
        [233, 4],
      ]) {
        const o = ctx.createOscillator();
        o.type = "sawtooth";
        o.detune.value = cents;
        o.frequency.setValueAtTime(freq * 1.12, t + at);
        o.frequency.exponentialRampToValueAtTime(freq, t + at + 0.05);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t + at);
        g.gain.exponentialRampToValueAtTime(0.5, t + at + 0.015);
        g.gain.setValueAtTime(0.5, t + at + length - 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t + at + length);
        o.connect(g).connect(shape);
        o.start(t + at);
        o.stop(t + at + length + 0.05);
      }
    }
  }

  /** White noise swept up over `beats`, with a rising whistle, into a drop. */
  function riser(t: number, beats: number) {
    const length = beats * BEAT;
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.Q.value = 2.5;
    band.frequency.setValueAtTime(300, t);
    band.frequency.exponentialRampToValueAtTime(9000, t + length);
    const swell = ctx.createGain();
    swell.gain.setValueAtTime(0.0001, t);
    swell.gain.exponentialRampToValueAtTime(0.5, t + length);
    swell.gain.setValueAtTime(0.0001, t + length);
    src.connect(band).connect(swell).connect(master);
    src.start(t);
    src.stop(t + length);
    const whistle = ctx.createOscillator();
    whistle.frequency.setValueAtTime(220, t);
    whistle.frequency.exponentialRampToValueAtTime(1760, t + length);
    const wg = ctx.createGain();
    wg.gain.setValueAtTime(0.0001, t);
    wg.gain.exponentialRampToValueAtTime(0.06, t + length);
    wg.gain.setValueAtTime(0.0001, t + length);
    whistle.connect(wg).connect(master);
    whistle.start(t);
    whistle.stop(t + length);
  }

  /** A snare roll over `beats`, doubling up as it goes. */
  function roll(t: number, beats: number) {
    for (let b = 0; b < beats; b++) {
      const per = b < beats / 2 ? 2 : b < beats - 1 ? 4 : 8;
      for (let i = 0; i < per; i++) {
        const along = (b + i / per) / beats;
        clap(t + (b + i / per) * BEAT, 0.12 + 0.4 * along);
      }
    }
  }

  /** Everything from beat `from` onwards, with beat `from` at audio time `at`. */
  function schedule(from: number, at: number) {
    const when = (beat: number) => at + (beat - from) * BEAT;
    for (let b = Math.ceil(from); b < PLAN.out + 1; b++) {
      const t = when(b);
      if (b < PLAN.drop) {
        if (b === PLAN.build) riser(t, PLAN.drop - b);
        if (b < 4) kick(t, 0.7);
        if (b === PLAN.build) roll(t, PLAN.drop);
        continue;
      }
      if (b >= PLAN.breakdown && b < PLAN.drop2) {
        if (b === PLAN.breakdown) {
          riser(t, PLAN.drop2 - b);
          roll(t, PLAN.drop2 - b);
          for (const freq of CHORDS[0]) tone("sawtooth", t, freq, 0.05, BEAT * 4, 7, BEAT * 2);
        }
        continue;
      }
      if (b === PLAN.out) {
        kick(t, 1);
        crash(t);
        const drop = tone("sine", t, 90, 0.6, 1.6);
        drop.frequency.exponentialRampToValueAtTime(28, t + 1.6);
        continue;
      }
      const bar = Math.floor((b - PLAN.drop) / 4);
      const inBar = (b - PLAN.drop) % 4;
      const chord = CHORDS[bar % 4];
      const finale = b >= PLAN.drop2;
      if (b === PLAN.drop || b === PLAN.drop2) {
        airhorn(t);
        crash(t);
      }
      kick(t);
      hat(t + BEAT / 2, finale && inBar === 3);
      if (finale) {
        hat(t + BEAT / 4);
        hat(t + (BEAT * 3) / 4);
      }
      if (inBar === 1 || inBar === 3) clap(t);
      bass(t + BEAT / 2, BASS[bar % 4]);
      // A 3-3-2 rhythm across the bar.
      if (inBar === 0) stab(t, chord);
      if (inBar === 1) stab(t + BEAT / 2, chord);
      if (inBar === 3) stab(t, chord, 0.05, 0.18);
      if (finale) for (let i = 0; i < 4; i++) arp(t + (i * BEAT) / 4, chord, inBar * 4 + i);
      if (b === PLAN.drop2 + 8) airhorn(t);
    }
    scheduled = true;
  }

  function startAtNextBeat() {
    if (scheduled || stopped) return;
    const sinceStart = (performance.now() - beat0) / 1000 / BEAT;
    // Too late to bother: the music's already over.
    if (sinceStart >= PLAN.out) return;
    const next = Math.max(0, Math.ceil(sinceStart + 0.05));
    const lead = (beat0 + next * BEAT * 1000 - performance.now()) / 1000;
    schedule(next, ctx.currentTime + Math.max(0.02, lead));
  }

  if (ctx.state === "running") startAtNextBeat();
  else ctx.resume().then(startAtNextBeat, () => {});

  return {
    levels() {
      if (!scheduled || ctx.state !== "running") return null;
      analyser.getByteFrequencyData(bins);
      return bins;
    },
    blocked: () => ctx.state !== "running" && !stopped,
    async unblock() {
      try {
        await ctx.resume();
      } catch {
        return false;
      }
      startAtNextBeat();
      return ctx.state === "running";
    },
    stop() {
      if (stopped) return;
      stopped = true;
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0, t + 0.3);
      setTimeout(() => ctx.close().catch(() => {}), 400);
    },
  };
}
